import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
} from "@angular/core";

import { BUTTON_DRAWING, type Draw } from "@hermes/aphrodite-button";
import { HermesSvg, SvgIconComponent } from "@hermes/aphrodite-svg";

import {
    ICON_BUTTON_COLOR,
    ICON_BUTTON_RADIUS,
    ICON_BUTTON_VARIANT,
    IconButtonColor,
    IconButtonRadius,
    IconButtonVariant,
    IconSvgSize,
} from "./icon-button.types";

/**
 * New h-icon-button to use.
 * This component implements the Hermes button with only icon presentation.
 *
 * Usage:
 *
 * ```ts
 *  <button hIconButton
 *    type="button"
 *    [variant]="BUTTON_VARIANT.FILLED"
 *    [radius]="BUTTON_RADIUS.SMALL"
 *    [color]="BUTTON_COLOR.BLACK"
 *    [iconName]="'arrow-left'"
 *    [iconSize]="['12px', '12px']"
 *    (click)="handleClick($event)"
 *  >
 *  </button>
 * ```
 */
@Component({
    selector: "button[hIconButton]",
    imports: [SvgIconComponent],
    templateUrl: "./icon-button.component.html",
    styleUrl: "./icon-button.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "button-icon",
        "[class.button-icon-variant-filled]": "isFilled()",
        "[class.button-icon-variant-outlined]": "isOutlined()",
        "[class.button-icon-variant-ghost]": "isGhost()",
        "[class.button-icon-color-primary]": "isPrimary()",
        "[class.button-icon-color-secondary]": "isSecondary()",
        "[class.button-icon-color-tertiary]": "isTertiary()",
        "[class.button-icon-radius-none]": "isRadiusNone()",
        "[class.button-icon-radius-small]": "isRadiusSmall()",
        "[class.button-icon-radius-round]": "isRadiusRound()",
        "[class.button-icon-active]": "active()",
    },
})
export class IconButtonComponent {
    /**
     * Icon button radius ("none", "small", "round", default value: "small")
     */
    public readonly radius = input<IconButtonRadius>(ICON_BUTTON_RADIUS.SMALL);

    /**
     * Icon button variant ("filled", "outlined", "ghost", default value: "filled")
     */
    public readonly variant = input<IconButtonVariant>(
        ICON_BUTTON_VARIANT.FILLED,
    );

    /**
     * Icon button color ("primary", "secondary" or "tertiary", default value: "primary")
     */
    public readonly color = input<IconButtonColor>(ICON_BUTTON_COLOR.PRIMARY);

    /**
     * [Required] Svg icon to display (HermesSvg)
     */
    public readonly iconName = input.required<HermesSvg>();
    public readonly iconDrawing = input<Draw | undefined>("stroke");
    /**
     * Icon svg viewbox size ([height, width], defaults value: ["12px", "12px"], max recommended value: ["24px", "24px"])
     */
    public readonly iconSize = input<IconSvgSize>(["12px", "12px"]);
    /**
     * When true, button visually looks like "focus" state styles
     */
    public readonly active = input(false, { transform: booleanAttribute });

    protected readonly isFilled = computed(
        () => this.variant() === ICON_BUTTON_VARIANT.FILLED,
    );

    protected readonly isOutlined = computed(
        () => this.variant() === ICON_BUTTON_VARIANT.OUTLINED,
    );

    protected readonly isGhost = computed(
        () => this.variant() === ICON_BUTTON_VARIANT.GHOST,
    );

    protected readonly isPrimary = computed(
        () => this.color() === ICON_BUTTON_COLOR.PRIMARY,
    );

    protected readonly isSecondary = computed(
        () => this.color() === ICON_BUTTON_COLOR.SECONDARY,
    );

    protected readonly isTertiary = computed(
        () => this.color() === ICON_BUTTON_COLOR.TERTIARY,
    );

    protected readonly isRadiusNone = computed(
        () => this.radius() === ICON_BUTTON_RADIUS.NONE,
    );

    protected readonly isRadiusSmall = computed(
        () => this.radius() === ICON_BUTTON_RADIUS.SMALL,
    );

    protected readonly isRadiusRound = computed(
        () => this.radius() === ICON_BUTTON_RADIUS.ROUND,
    );
    protected readonly DRAWING = BUTTON_DRAWING;
}
